// ./src/hooks/useEventTickets.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  EventTicket,
  EventTicketService,
  EventTicketFilter,
  EventTicketInput,
  EventTicketBulkInput,
  TicketReassignGuestInput,
  TicketRefundInput,
  TicketGenerateInput,
  TICKET_STATE,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseEventTicketsOptions {
  pageSize?: number;
  initialFilters?: Partial<EventTicketFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
}

export interface UseEventTicketsReturn {
  ticket: EventTicket | null;
  tickets: EventTicket[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  isBulkUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;
  actingIds: Set<string>;
  isActing: (id?: string) => boolean;

  filters: Partial<EventTicketFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<EventTicketFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  create: (params: EventTicketInput) => Promise<OpResult>;
  update: (id: string, params: EventTicketInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  bulkUpdate: (ids: string[], params: EventTicketBulkInput) => Promise<OpResult>;

  scan: (id: string, action: string) => Promise<OpResult>;
  markSold: (id: string) => Promise<OpResult>;
  reassign: (id: string, guest: TicketReassignGuestInput) => Promise<OpResult>;
  changeType: (id: string, ticketTypeId: string) => Promise<OpResult>;
  refund: (id: string, params: TicketRefundInput) => Promise<OpResult>;
  isGenerating: boolean;
  generate: (params: TicketGenerateInput) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => EventTicket | undefined;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// Newest issued/sold tickets first (matches the server default sort).
const DEFAULT_SORT: SortItem[] = [{ name: "createdAt", order: "DESC" }];
const DEFAULT_FILTERS: Partial<EventTicketFilter> = {};

export const useEventTickets = (options: UseEventTicketsOptions = {}): UseEventTicketsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<EventTicketFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [ticket, setTicket] = useState<EventTicket | null>(null);
  const [tickets, setTickets] = useState<EventTicket[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState<Partial<EventTicketFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [actingIds, setActingIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && tickets.length === 0, [isLoaded, tickets.length]);

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const startActing = useCallback((id: string) => {
    setActingIds((prev) => {
      const next = new Set(prev);
      next.add(id);

      return next;
    });
  }, []);

  const stopActing = useCallback((id: string) => {
    setActingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);

      return next;
    });
  }, []);

  const isActing = useCallback(
    (id?: string): boolean => (id ? actingIds.has(id) : actingIds.size > 0),
    [actingIds]
  );

  // Custom per-ticket actions (scan / reassign / change type / refund) all return
  // the updated ticket; upsert it into the list so the row reflects the change
  // without a full reload. A shared runner keeps the in-flight + error handling
  // identical across the four.
  const runTicketAction = useCallback(
    async (id: string, run: () => Promise<OpResult>): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Ticket ID is required");
        setLastError(result);

        return result;
      }

      startActing(id);
      setLastError(null);

      try {
        const result = await run();

        if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) && result.data.length > 0) {
          const updated = (result.data as EventTicket[])[0];
          setTicket(updated);
          setTickets((prev) => upsertBy(prev, updated));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopActing(id);
      }
    },
    [startActing, stopActing]
  );

  const scan = useCallback(
    (id: string, action: string): Promise<OpResult> => runTicketAction(id, () => EventTicketService.scan(id, action)),
    [runTicketAction]
  );

  const markSold = useCallback(
    (id: string): Promise<OpResult> => runTicketAction(id, () => EventTicketService.update({ id }, { ticketState: TICKET_STATE.SOLD })),
    [runTicketAction]
  );

  const reassign = useCallback(
    (id: string, guest: TicketReassignGuestInput): Promise<OpResult> =>
      runTicketAction(id, () => EventTicketService.reassign(id, guest)),
    [runTicketAction]
  );

  const changeType = useCallback(
    (id: string, ticketTypeId: string): Promise<OpResult> =>
      runTicketAction(id, () => EventTicketService.changeType(id, ticketTypeId)),
    [runTicketAction]
  );

  const refund = useCallback(
    (id: string, params: TicketRefundInput): Promise<OpResult> =>
      runTicketAction(id, () => EventTicketService.refund(id, params)),
    [runTicketAction]
  );

  const generate = useCallback(async (params: TicketGenerateInput): Promise<OpResult> => {
    setIsGenerating(true);
    setLastError(null);

    try {
      const result = await EventTicketService.generate(params);

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        const newItems = result.data as EventTicket[];
        setTickets((prev) => upsertManyBy(prev, newItems, "id", "start"));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setTicket(null);
    setTickets([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setRemovingIds(new Set());
  }, []);

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await EventTicketService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as EventTicket[])[0];
        setTicket(data);
        setTickets((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setTicket(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setTicket(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<EventTicketFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);
      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          EventTicketService.list,
          filterOverride as EventTicketFilter,
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as EventTicket[]) : [];

          if (isAppending) {
            setTickets((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setTickets(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTickets([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTickets([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const create = useCallback(async (params: EventTicketInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await EventTicketService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as EventTicket[])[0];
        setTicket(newItem);
        setTickets((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: EventTicketInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Ticket ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await EventTicketService.update({ id }, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as EventTicket[])[0];
        setTicket(updatedItem);
        setTickets((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Ticket ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await EventTicketService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTicket(null);
          setTickets((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one ticket must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await EventTicketService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setTicket(null);
          setTickets((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const bulkUpdate = useCallback(
    async (ids: string[], params: EventTicketBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one ticket must be selected");
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await EventTicketService.bulkUpdate(ids, params);

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data) ? (result.data as EventTicket[]) : [];
          setTickets((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): EventTicket | undefined => {
      if (!id) {
        return undefined;
      }

      return tickets.find((item) => item.id === id);
    },
    [tickets]
  );

  const totalCount = useMemo(() => tickets.length, [tickets]);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  return useMemo(
    () => ({
      ticket,
      tickets,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      actingIds,
      isActing,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      scan,
      markSold,
      reassign,
      changeType,
      refund,
      isGenerating,
      generate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      ticket,
      tickets,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      actingIds,
      isActing,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      scan,
      markSold,
      reassign,
      changeType,
      refund,
      isGenerating,
      generate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
